/** biome-ignore-all lint/style/noNonNullAssertion: ignore */
import store from 'store';
/**
 * Configuration for delegation token authentication
 */
export interface DelegationTokenConfig {
  baseUrl: string;
  /** The endpoint to fetch delegation tokens from */
  endpoint: string;
  /** Additional headers to send with token requests (can be function for dynamic headers) */
  headers?: Record<string, string> | (() => Record<string, string>);
  /** Custom token validation logic */
  isValid?: (token: string) => boolean;
  /** Accounts endpoint detection configuration */
  accountsEndpoints: {
    /** Hostnames that serve accounts APIs */
    // hostnames?: string[];
    /** URL path patterns that identify accounts endpoints */
    pathPatterns: string[];
    /** Base URL for accounts APIs */
    baseUrl?: string;
  };
}

/**
 * Get client-side environment variables
 * These should be prefixed with PUBLIC_ to be available in the browser via import.meta.env
 */
const getEnvVar = (key: string, defaultValue?: string): string | undefined => {
  // Rsbuild replaces import.meta.env.PUBLIC_* at build time
  const envVars: Record<string, string | undefined> = {
    TEAMLOCKER_SESSION_ENDPOINT: import.meta.env.PUBLIC_TEAMLOCKER_SESSION_ENDPOINT,
    ACCOUNTS_API_BASE_URL: import.meta.env.PUBLIC_ACCOUNTS_API_BASE_URL,
    ACCOUNTS_API_PATHS: import.meta.env.PUBLIC_ACCOUNTS_API_PATHS,
    REACT_QUERY_STALE_TIME: import.meta.env.PUBLIC_REACT_QUERY_STALE_TIME,
    REACT_QUERY_RETRY: import.meta.env.PUBLIC_REACT_QUERY_RETRY,
    OAUTH_AUTHORIZE_URL: import.meta.env.PUBLIC_OAUTH_AUTHORIZE_URL,
    OAUTH_CLIENT_ID: import.meta.env.PUBLIC_OAUTH_CLIENT_ID,
    OAUTH_REDIRECT_URI: import.meta.env.PUBLIC_OAUTH_REDIRECT_URI,
    RECAPTCHA_SITE_KEY: import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY,
    BYPASS_RECAPTCHA: import.meta.env.PUBLIC_BYPASS_RECAPTCHA,
    LAUNCH_DARKLY_CLIENT_ID: import.meta.env.PUBLIC_LAUNCH_DARKLY_CLIENT_ID,
    TEAMLOCKER_BASE_URL: import.meta.env.PUBLIC_TEAMLOCKER_BASE_URL,
    ADMIN_DASH_BASE_URL: import.meta.env.PUBLIC_ADMIN_DASH_BASE_URL,
    POST_LOGOUT_REDIRECT_URL: import.meta.env.PUBLIC_POST_LOGOUT_REDIRECT_URL,
    AWS_S3_BASE_URL: import.meta.env.PUBLIC_AWS_S3_BASE_URL,
    HUBSPOT_ACCOUNT_ID: import.meta.env.PUBLIC_HUBSPOT_ACCOUNT_ID,
    SESSION_RECHECK_INTERVAL_SECONDS: import.meta.env.PUBLIC_SESSION_RECHECK_INTERVAL_SECONDS,
    TOUR_START_DELAY: import.meta.env.PUBLIC_TOUR_START_DELAY,
    TOUR_NEED_HELP_START_DELAY: import.meta.env.PUBLIC_TOUR_NEED_HELP_START_DELAY,
    DATADOG_ENABLED: import.meta.env.PUBLIC_DATADOG_ENABLED,
    DATADOG_CLIENT_TOKEN: import.meta.env.PUBLIC_DATADOG_CLIENT_TOKEN,
    DATADOG_SITE: import.meta.env.PUBLIC_DATADOG_SITE,
    DATADOG_SERVICE: import.meta.env.PUBLIC_DATADOG_SERVICE,
    DATADOG_ENV: import.meta.env.PUBLIC_DATADOG_ENV,
    DATADOG_VERSION: import.meta.env.PUBLIC_DATADOG_VERSION,
    DATADOG_SESSION_SAMPLE_RATE: import.meta.env.PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
    DATADOG_VERBOSE: import.meta.env.PUBLIC_DATADOG_VERBOSE,
    LOG_ROUTE_CHANGES: import.meta.env.PUBLIC_LOG_ROUTE_CHANGES,
    GOOGLE_PLACES_API_KEY: import.meta.env.PUBLIC_GOOGLE_PLACES_API_KEY,
    HEAP_ENV_ID: import.meta.env.PUBLIC_HEAP_ENV_ID,
    HIDE_TANSTACK_ROUTER_DEVTOOLS: import.meta.env.PUBLIC_HIDE_TANSTACK_ROUTER_DEVTOOLS,
  };
  return envVars[key] ?? defaultValue;
};

/**
 * Default configuration for delegation token
 * Customize this based on your API requirements
 */
export const delegationTokenConfig: DelegationTokenConfig = {
  baseUrl: getEnvVar('TEAMLOCKER_SESSION_ENDPOINT', '/session')!,
  endpoint: `${getEnvVar('TEAMLOCKER_SESSION_ENDPOINT', '/session')!}/delegation-token`, // Use relative path - proxy will route to TeamLocker API
  headers: () => {
    // Dynamic headers - called each time a request is made
    const oauthToken = store.get('sqd_oauth_token');
    return {
      'Content-Type': 'application/json',
      ...(oauthToken ? { Authorization: `Bearer ${oauthToken.authenticated.access_token}` } : {}),
    };
  },
  accountsEndpoints: {
    pathPatterns: ['/storefront/', '/storefront/api/', ...(getEnvVar('ACCOUNTS_API_PATHS')?.split(',').filter(Boolean) || [])],
    baseUrl: getEnvVar('ACCOUNTS_API_BASE_URL'),
  },
};

export const hideTanstackDevTools = getEnvVar('HIDE_TANSTACK_ROUTER_DEVTOOLS', '') === 'true';

export const reactQueryConfig = {
  staleTime: getEnvVar('REACT_QUERY_STALE_TIME') ? parseInt(getEnvVar('REACT_QUERY_STALE_TIME')!, 10) : 5 * 60 * 1000, // 5 minutes
  cacheTime: 30 * 60 * 1000, // 30 minutes
  retry: getEnvVar('REACT_QUERY_RETRY')
    ? parseInt(getEnvVar('REACT_QUERY_RETRY')!, 10) !== 0
      ? parseInt(getEnvVar('REACT_QUERY_RETRY')!, 10)
      : false
    : false,
};

/**
 * OAuth2 Implicit Flow Configuration
 */
export const oauthConfig = {
  authorizeUrl: getEnvVar('OAUTH_AUTHORIZE_URL', 'http://localhost:5000/oauth/authorize')!,
  clientId: getEnvVar('OAUTH_CLIENT_ID', 'aaa711ad-6854-4792-b938-ce90c92935a5')!,
  redirectUri: getEnvVar('OAUTH_REDIRECT_URI', 'http://localhost:4000/oauth/callback')!,
  responseType: 'token' as const,
  storageKey: 'sqd_oauth_token' as const,
};

export const recaptchaConfig = {
  bypassRecaptcha: getEnvVar('BYPASS_RECAPTCHA', 'false') === 'true',
  siteKey: getEnvVar('RECAPTCHA_SITE_KEY', ''),
};

export const launchDarklyConfig = {
  clientId: getEnvVar('LAUNCH_DARKLY_CLIENT_ID', ''),
};

export const teamLockerConfig = {
  baseUrl: getEnvVar('TEAMLOCKER_BASE_URL', ''),
};

export const adminDashConfig = {
  baseUrl: getEnvVar('ADMIN_DASH_BASE_URL', ''),
};

export const logoutLandingConfig = {
  url: getEnvVar('POST_LOGOUT_REDIRECT_URL', ''),
};

export const awsS3Config = {
  baseUrl: getEnvVar('AWS_S3_BASE_URL', 'https://teamlocker-qa-2.s3.amazonaws.com'),
};

export const hubspotConfig = {
  accountId: getEnvVar('HUBSPOT_ACCOUNT_ID', '51317695'),
};

export const sessionRecheckConfig = {
  intervalSeconds: +(getEnvVar('SESSION_RECHECK_INTERVAL_SECONDS') ?? '') > 0 ? parseInt(getEnvVar('SESSION_RECHECK_INTERVAL_SECONDS')!, 10) : 30,
};

export const tourConfig = {
  startDelay: getEnvVar('TOUR_START_DELAY') ? parseInt(getEnvVar('TOUR_START_DELAY')!, 10) : 1500, // Delay before starting the tour (in milliseconds)
  helpTourStartDelay: getEnvVar('TOUR_NEED_HELP_START_DELAY') ? parseInt(getEnvVar('TOUR_NEED_HELP_START_DELAY')!, 10) : 1000, // Delay before showing the help tour after dismissing the main tour
};

export const datadogConfig = {
  enabled: getEnvVar('DATADOG_ENABLED', 'false') === 'true' && !!getEnvVar('DATADOG_CLIENT_TOKEN'),
  clientToken: getEnvVar('DATADOG_CLIENT_TOKEN', '')!,
  site: getEnvVar('DATADOG_SITE', 'datadoghq.com')!,
  service: getEnvVar('DATADOG_SERVICE', 'storebuilder')!,
  env: getEnvVar('DATADOG_ENV', 'development')!,
  version: getEnvVar('DATADOG_VERSION', '0.0.0')!,
  sessionSampleRate: getEnvVar('DATADOG_SESSION_SAMPLE_RATE') ? parseInt(getEnvVar('DATADOG_SESSION_SAMPLE_RATE')!, 10) : 100,
  verbose: getEnvVar('DATADOG_VERBOSE', 'false') === 'true',
};

export const loggingConfig = {
  logRouteChanges: getEnvVar('LOG_ROUTE_CHANGES', 'false') === 'true',
};

export const googlePlacesConfig = {
  apiKey: getEnvVar('GOOGLE_PLACES_API_KEY', ''),
};

export const heapConfig = {
  envId: getEnvVar('HEAP_ENV_ID', ''),
};

/**
 * Build the OAuth authorize URL for redirecting users to login
 */
export const buildOAuthAuthorizeUrl = (): string => {
  const params = new URLSearchParams({
    response_type: oauthConfig.responseType,
    client_id: oauthConfig.clientId,
    redirect_uri: oauthConfig.redirectUri,
  });
  return `${oauthConfig.authorizeUrl}?${params.toString()}`;
};
